import Reveal from "@/components/Reveal";
import { KeyRound, HeartHandshake, Sofa, PieChart, ShieldCheck } from "lucide-react";

const IMAGE = "https://images.pexels.com/photos/34277650/pexels-photo-34277650.jpeg";

const POINTS = [
  {
    icon: KeyRound,
    title: "You Keep Ownership & Control.",
    text: "Your property remains yours — no lease required. We manage the hosting operations on your behalf.",
  },
  {
    icon: PieChart,
    title: "Transparent 80/20 Profit Sharing.",
    text: "You receive 80% of monthly rental profits after agreed operating expenses. We earn 20% for managing the rental.",
  },
  {
    icon: Sofa,
    title: "Hands-Off Hosting.",
    text: "From the first booking inquiry to the final guest checkout, our team handles the day-to-day details.",
  },
  {
    icon: HeartHandshake,
    title: "Professional Guest Experience.",
    text: "Thoughtful communication, organized check-ins, and dependable hospitality.",
  },
  {
    icon: ShieldCheck,
    title: "Property Care Matters.",
    text: "We prioritize cleaning standards, guest screening practices, property protection, and proactive communication.",
  },
];

export default function WhyUs() {
  return (
    <section id="why-us" className="py-20 sm:py-28" data-testid="why-us-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <Reveal>
          <div className="relative">
            <div className="rounded-[2rem] overflow-hidden shadow-[0_24px_60px_rgba(0,0,0,0.12)]">
              <img
                src={IMAGE}
                alt="Spacious welcoming living room of a professionally managed short-term rental"
                className="w-full h-[320px] sm:h-[480px] object-cover"
                data-testid="why-us-image"
              />
            </div>
            <div className="absolute -bottom-6 right-6 bg-[#FF5A5F] text-white rounded-2xl px-6 py-4 shadow-[0_12px_32px_rgba(255,90,95,0.4)]">
              <p className="text-xs font-bold tracking-widest uppercase opacity-80">Keep 80% of Profits</p>
              <p className="text-sm font-bold mt-1">We handle the management</p>
            </div>
          </div>
        </Reveal>

        <div>
          <Reveal>
            <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#FF5A5F]">Why Landlords Partner With Us</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-[#222]">
              Your Property Should Make You Money — Not More Work.
            </h2>
            <p className="mt-5 text-base sm:text-lg leading-relaxed text-[#5a5a5a]">
              Between vacancies, ongoing expenses, guest communication, and property upkeep, managing a
              rental can quickly become overwhelming.
            </p>
            <p className="mt-4 text-base sm:text-lg leading-relaxed text-[#5a5a5a]">
              At Shonta Solutions, we take that responsibility off your hands. We help attract guests,
              manage bookings, coordinate cleanings, and oversee daily rental operations so you can focus
              on enjoying the benefits of property ownership.
            </p>
          </Reveal>
          <ul className="mt-10 space-y-6">
            {POINTS.map((point, i) => (
              <Reveal key={point.title} delay={i * 0.08}>
                <li className="flex gap-5" data-testid={`why-us-point-${i + 1}`}>
                  <span className="shrink-0 w-12 h-12 rounded-2xl bg-[#FFD3C6]/60 flex items-center justify-center">
                    <point.icon className="w-5 h-5 text-[#E0484D]" />
                  </span>
                  <div>
                    <h3 className="text-lg font-bold text-[#222]">{point.title}</h3>
                    <p className="mt-1 text-[15px] leading-relaxed text-[#5a5a5a]">{point.text}</p>
                  </div>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
